/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { jsx, css } from "@emotion/core"
import { Input, AutoComplete } from "@duxton-web/ui"
import { Form } from "grab-ui"
import { SearchUniversal } from "@duxton-web/icons"
import { compact, find, isEmpty } from "lodash"
import { FC, useEffect } from "react"
import { useSelector } from "react-redux"
import { useLocation } from "react-router-dom"
import { AppState } from "store/store.type"
import { Item } from "types/models/menu"
import { ItemName } from "components/ItemName"
import { storageHelper } from "utils/storage"
import { useTranslation } from "common/utils/i18n"
import { TroyWebTracker } from "utils/tracking"
import { EventAction, EventCategory } from "constants/tracking"
import { useEnablePartialMenu } from "hooks/useEnablePartialMenu"
import { useMenuV3HierarchiesInfinite } from "queries/partialMenuApi"
import { SearchNotFound } from "./SearchNotFound"

import {
  SEARCH_FIELD,
  FILTER_LIMIT,
  RECENT_LIMIT,
  SearchCategory,
} from "./searchBarConstants"

interface SearchBarProps extends FormComponentProps {
  className?: string
  itemsFromParent?: Item[]
  onSelect?(item: Item): void
  onClear?(): void
  onSearch?(value: any): void
  hideDropdownResults?: boolean
}

export const ItemSearchBarComponent: FC<SearchBarProps> = ({
  form,
  className,
  itemsFromParent,
  onSelect,
  onClear,
  onSearch,
  hideDropdownResults,
}: SearchBarProps) => {
  const { setFieldsValue, getFieldDecorator, getFieldValue, resetFields } = form

  const {
    merchantID,
    overview: { itemID },
    items,
    menuGroupDetail: { menuGroupID },
    merchantGroupID,
  } = useSelector((state: AppState) => state.menu)

  const enablePartialMenu = useEnablePartialMenu()

  const { hasDepartments } = useMenuV3HierarchiesInfinite(
    { merchantID, merchantGroupID },
    {
      skip: !enablePartialMenu || !merchantID,
      selectFromResult: result => ({
        hasDepartments: !!result.currentData?.pages?.[0]?.hierarchies?.length,
      }),
    },
  )

  const recentItemIDs = storageHelper.getMenuSearchHistory(merchantID)

  const location = useLocation()
  const eventCategory = location.pathname.endsWith("/items")
    ? EventCategory.menuItemsTab
    : EventCategory.menuOverviewTab
  const handleClickSearch = () => {
    TroyWebTracker.sendEvent({
      category: eventCategory,
      action: EventAction.clickItemSearch,
      labels: {
        ...(merchantID
          ? { STORE_ID: merchantID }
          : { MERCHANT_GROUP_ID: merchantGroupID, MENU_GROUP_ID: menuGroupID }),
        IS_PARTIAL_MENU: enablePartialMenu,
      },
    })
  }

  const handleSelect = (
    _: string,
    option: {
      props: {
        item: Item
        index: number
        searchCategory?: SearchCategory
      }
    },
  ) => {
    const { item, index, searchCategory } = option.props
    // small hack
    setTimeout(() => {
      setFieldsValue({
        [SEARCH_FIELD]: item?.itemName,
      })
    }, 0)

    if (!item) return

    storageHelper.setMenuSearchHistory({
      merchantID,
      itemIDs: compact([item.itemID, ...recentItemIDs]),
    })

    TroyWebTracker.sendEvent({
      category: eventCategory,
      action: EventAction.selectItemSearchResults,
      labels: {
        SEARCH_TEXT: getFieldValue(SEARCH_FIELD),
        SEARCH_RESULT_RANK: index + 1,
        SEARCH_CATEGORY: searchCategory ?? null,
        ITEM_ID: item.itemID,
        IS_PARTIAL_MENU: enablePartialMenu,
      },
    })

    onSelect?.(item)
  }

  const handleClearInput = () => {
    resetFields()
    onClear?.()
  }

  const handleItemIDReset = () => {
    if (isEmpty(itemID)) {
      handleClearInput()
    }
  }

  useEffect(handleItemIDReset, [itemID])

  const renderOption = (
    item: Item,
    index: number,
    searchCategory?: SearchCategory,
    highlightSubstring?: boolean,
  ) => {
    return {
      key: `${searchCategory}_${item.itemID}`,
      value: `${searchCategory}_${item.itemID}`,
      item,
      index,
      searchCategory,
      label: (
        <div
          css={css`
            font-size: 14px;
            &:not(:hover) {
              .edit_hover_class {
                visibility: hidden;
              }
            }
          `}
        >
          <ItemName
            item={item}
            searchString={highlightSubstring && getFieldValue(SEARCH_FIELD)}
          />
          <div>
            <span
              css={css`
                color: #979797;
                font-size: 12px;
              `}
            >
              {item.sectionName}
            </span>
          </div>
        </div>
      ),
    }
  }

  const filteredItems = getFieldValue(SEARCH_FIELD)
    ? (itemsFromParent ?? items)
        .filter(item =>
          item.itemName
            ?.toLowerCase()
            .includes(getFieldValue(SEARCH_FIELD)?.toLowerCase()),
        )
        .slice(0, FILTER_LIMIT)
    : []

  // @ts-ignore
  const recentItems: Item[] = recentItemIDs
    .map(ID => find(itemsFromParent ?? items, item => item.itemID === ID))
    .filter(item => !isEmpty(item))
    .slice(0, RECENT_LIMIT)

  const recentlySearchedTrans = useTranslation("menu.overview.recentlySearched")

  const dataSource = compact([
    // Not found placeholder
    !filteredItems?.length &&
      getFieldValue(SEARCH_FIELD) && {
        key: "not found",
        value: "not found",
        disabled: true,
        label: <SearchNotFound search={getFieldValue(SEARCH_FIELD)} />,
      },
    // Filtered data
    ...filteredItems.map((item, index) =>
      renderOption(item, index, SearchCategory.results, true),
    ),
    // Recent data
    recentItems?.length && {
      key: SearchCategory.recent,
      label: (
        <span
          css={css`
            font-weight: 500;
            font-size: 12px;
            line-height: 14px;
            letter-spacing: 0.5px;
            text-transform: uppercase;
          `}
        >
          {recentlySearchedTrans}
        </span>
      ),
      options: recentItems.map((item, index) =>
        renderOption(item, index, SearchCategory.recent),
      ),
    },
  ])

  return (
    <div className={className}>
      {getFieldDecorator(SEARCH_FIELD, {
        initialValue: "",
      })(
        <AutoComplete
          data-testid="auto-complete"
          css={css`
            width: 100%;
          `}
          options={hideDropdownResults ? [] : dataSource}
          onSelect={handleSelect}
          onSearch={onSearch}
          disabled={enablePartialMenu ? !hasDepartments : !items.length}
          allowClear
          onClear={handleClearInput}
        >
          <Input
            data-testid="input"
            autoComplete="off"
            placeholder={useTranslation("menu.overview.placeholder.searchItem")}
            prefix={<SearchUniversal />}
            onClick={handleClickSearch}
          />
        </AutoComplete>,
      )}
    </div>
  )
}
export const ItemSearchBar = Form.create<SearchBarProps>({})(
  ItemSearchBarComponent,
)
